import { Pressable, Platform } from 'react-native';
import { Menu, MenuItem, MenuItemLabel } from '@/components/gluestack/menu';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import {
  MoreHorizontalIcon,
  Cancel01Icon,
} from '@hugeicons-pro/core-solid-rounded';
import {
  PencilEdit02Icon,
  Alert02Icon,
  Delete02Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { MenuItemIcon } from '@/components/ui/MenuItemIcon';
import { Comment } from '@/models/Comment';
import { Post } from '@/models/Post';
import { Photo } from '@/models/Photo';
import { plainToClass } from '@/models/transform';
import { useTranslation } from 'react-i18next';
import { useSupportService } from '@/hooks/useSupportService';
import { ReportReasonSheet } from '@/components/domain/common/ReportReasonSheet';
import { useState } from 'react';
import { CommentsService } from '@/services/domain/CommentsService';
import { useToastMessages } from '@/hooks/useToastMessages';
import { ContextSheet } from '@/components/ui/ContextSheet';
import { EditComment } from './EditComment';
import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogCloseButton,
  AlertDialogBody,
  AlertDialogFooter,
} from '@/components/gluestack/alert-dialog';
import { Button, ButtonText } from '@/components/gluestack/button';
import { Text } from '@/components/gluestack/text';
import { Heading } from '@/components/gluestack/heading';
import { useQueryClient } from '@tanstack/react-query';
import { useQueryCacheUtils } from '@/hooks/useQueryCacheUtils';

interface ICommentContextMenuProps {
  comment: Comment;
  onCommentEdited?: (comment: Comment) => void;
  parentPost?: Post;
  parentPhoto?: Photo;
}

export function CommentContextMenu({ comment, onCommentEdited, parentPost, parentPhoto }: ICommentContextMenuProps) {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const queryClient = useQueryClient();
  const { syncPostFromServer } = useQueryCacheUtils();
  const { submitPolicyViolationForComment } = useSupportService();
  const [isLoading, setIsLoading] = useState(false);
  const [showEditCommentSheet, setShowEditCommentSheet] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const [showReportReasonSheet, setShowReportReasonSheet] = useState(false);

  const handleReport = () => {
    setShowReportReasonSheet(true);
  };

  const handleEdit = () => {
    setShowEditCommentSheet(true);
  };
  
  const handleCommentEdited = (editedComment: Comment) => {
    onCommentEdited?.(editedComment);
    setShowEditCommentSheet(false);
  };

  const handleOpenDeleteConfirmation = () => {
    setShowDeleteConfirmation(true);
  };

  const handleDelete = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await CommentsService.deleteComment(comment.Id);
      
      // Invalidate comments list to refresh
      await queryClient.invalidateQueries({
        queryKey: ['comments'],
      });
      
      if (parentPost) {
        await syncPostFromServer(parentPost.Id);
      }
      
      if (parentPhoto) {
        // Update photo caches (use setQueriesData to match all photo queries with this ID)
        queryClient.setQueriesData({ queryKey: ['photo', parentPhoto.Id] }, (oldData: Photo | undefined) => {
          if (!oldData) return oldData;
          return plainToClass(Photo, { ...oldData, CommentsCount: Math.max(0, (oldData.CommentsCount ?? 0) - 1) });
        });
        // Update photos list caches
        queryClient.setQueriesData({ queryKey: ['photos'] }, (oldData: any) => {
          if (!oldData?.pages) return oldData;
          const newPages = oldData.pages.map((page: any) => ({
            ...page,
            PhotosItems: page.PhotosItems?.map((ph: Photo) =>
              ph.Id === parentPhoto.Id ? plainToClass(Photo, { ...ph, CommentsCount: Math.max(0, (ph.CommentsCount ?? 0) - 1) }) : ph
            ) ?? page.PhotosItems,
          }));
          const updates: any = { ...oldData, pages: newPages };
          if (oldData.items) {
            updates.items = newPages.flatMap((page: any) => page.PhotosItems ?? []);
          }
          return updates;
        });
      }
      
      showSuccessToast(t('comments.delete.success'));
    } catch (error) {
      showErrorToast(t('comments.delete.error'), error);
    } finally {
      setIsLoading(false);
      setShowDeleteConfirmation(false);
    }
  };

  return (
    <>
      <Menu
        placement="bottom right"
        trigger={(props) => (
          <Pressable
            {...props}
            className="flex h-6 w-6 items-center justify-center"
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <HugeIconsWrapper icon={MoreHorizontalIcon} size={20} className="text-typography-700" />
          </Pressable>
        )}>
        {comment.IsAuthor && !comment.HasMedia && (
          <MenuItem key="edit" textValue={t('comments.menu.edit')} onPress={handleEdit}>
            <MenuItemIcon icon={PencilEdit02Icon} />
            <MenuItemLabel>{t('comments.menu.edit')}</MenuItemLabel>
          </MenuItem>
        )}
        {!comment.IsAuthor && (
          <MenuItem key="report" textValue={t('comments.menu.report')} onPress={handleReport}>
            <MenuItemIcon icon={Alert02Icon} className="color-error-500" />
            <MenuItemLabel>{t('comments.menu.report')}</MenuItemLabel>
          </MenuItem>
        )}
        {(comment.IsAuthor ||
          comment.CanDelete ||
          (Platform.OS === 'web' && comment.CanModerate)) && (
          <MenuItem 
            key="delete" 
            textValue={t('comments.menu.delete')} 
            onPress={handleOpenDeleteConfirmation}
            disabled={isLoading}>
            <MenuItemIcon icon={Delete02Icon} className="color-error-500" />
            <MenuItemLabel>{t('comments.menu.delete')}</MenuItemLabel>
          </MenuItem>
        )}
      </Menu>

      <ContextSheet
        isOpen={showEditCommentSheet}
        onClose={() => setShowEditCommentSheet(false)}
        contentComponent={EditComment}
        title={t('comments.menu.edit')}
        payload={{ comment, parentPost, onCommentEdited: handleCommentEdited }}
      />

      <AlertDialog
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        size="md">
        <AlertDialogBackdrop />
        <AlertDialogContent>
          <AlertDialogHeader>
            <Heading size="md">{t('comments.delete.confirmTitle')}</Heading>
            <AlertDialogCloseButton className="">
              <HugeIconsWrapper icon={Cancel01Icon} size={20} className="color-neutral-500" />
            </AlertDialogCloseButton>
          </AlertDialogHeader>
          <AlertDialogBody className="mt-3">
            <Text>{t('comments.delete.confirmMessage')}</Text>
          </AlertDialogBody>
          <AlertDialogFooter className="mt-6 gap-3">
            <Button
              variant="outline"
              action="neutral"
              onPress={() => setShowDeleteConfirmation(false)}
              disabled={isLoading}>
              <ButtonText>{t('common.cancel')}</ButtonText>
            </Button>
            <Button action="negative" onPress={handleDelete} disabled={isLoading}>
              <ButtonText>{t('common.delete')}</ButtonText>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ReportReasonSheet
        isOpen={showReportReasonSheet}
        onClose={() => setShowReportReasonSheet(false)}
        subject="comment"
        onReasonSelected={(reason) => submitPolicyViolationForComment(comment, reason)}
      />
    </>
  );
}
